import type { ThemeDefinition } from 'vuetify'

// --- Static Colors ---
export const staticPrimaryColor = '#7366DB'
export const staticPrimaryDarkenColor = '#7366DB'
export const staticErrorColor = '#F04438'

// --- Shared Variables ---
const shared = {
  // Spacing
  'spacing-xs': '4px',
  'spacing-sm': '6px',
  'spacing-md': '8px',
  'spacing-lg': '12px',
  'spacing-xl': '16px',
  'spacing-2xl': '20px',
  'spacing-3xl': '24px',
  'spacing-4xl': '32px',
  'spacing-5xl': '40px',

  // Radius
  'radius-sm': '6px',
  'radius-md': '8px',
  'radius-full': '9999px',
  'radius-4xl': '24px',
}

// --- Theme Definitions ---
export const themes: Record<string, ThemeDefinition> = {
  light: {
    dark: false,
    colors: {
      // Standard Colors
      'primary': staticPrimaryColor,
      'on-primary': '#FFFFFF',
      'primary-darken-1': '#9D9AEA', // Lighter shade for hover/active
      'primary-darken-2': '#5C52AF', // Darker shade for hover/active
      'secondary': '#737373', // Mapped from Gray Iron 400
      'on-secondary': '#FFFFFF',
      'secondary-darken-1': '#D4D4D4', // Mapped from Gray Iron 600
      'error': staticErrorColor,
      'on-error': '#FFFFFF',
      'error-darken-1': '#D92D20', // Darker shade for hover/active
      'success': '#12B76A', // Retained from old theme for completeness
      'on-success': '#FFFFFF',
      'success-darken-1': '#0E9355',
      'info': '#00BAD1', // Retained from old theme for completeness
      'on-info': '#FFFFFF',
      'info-darken-1': '#00A7BC',
      'warning': '#FF9F43', // Retained from old theme for completeness
      'on-warning': '#FFFFFF',
      'warning-darken-1': '#E68F3C',

      // Backgrounds & Surfaces
      'background': '#fff', // Mapped from BG
      'on-background': '#171717', // Mapped from Text
      'surface': '#FFFFFF', // Mapped from Setting_Nav
      'on-surface': '#171717', // Mapped from Text

      // Gray Iron Scale (Light)
      'grey-50': '#171717',
      'grey-100': '#262626',
      'grey-200': '#404040',
      'grey-300': '#525252',
      'grey-400': '#737373',
      'grey-500': '#A3A3A3',
      'grey-600': '#D4D4D4',
      'grey-700': '#E5E5E5',
      'grey-800': '#F5F5F5',
      'grey-900': '#FAFAFA',
      'grey-950': '#FFFFFF',

      // Brand Scale (Light)
      'brand-50': '#3A336E',
      'brand-100': '#453D83',
      'brand-200': '#514799',
      'brand-300': '#5C52AF',
      'brand-400': '#685CC5',
      'brand-600': '#8781E3',
      'brand-700': '#9D9AEA',
      'brand-800': '#B4B4F1',
      'brand-900': '#CCCDF6',
      'brand-950': '#E5E6FB',

      // Custom App-Specific Colors (Light)
      'white': '#FFFFFF',
      'black': '#000000',
      'setting-bg': '#F5F5F5',
      'setting-nav': '#FFFFFF',
      'login-bg': '#FFFFFF',
      'button-bg': '#EFEFEF',
      'toggle-bg': '#FFFFFF',
      'link-color': staticPrimaryColor,
      'clear-day': '#EFF4FF',
      'search-bar': '#f1f1f1',
      'pro-background': '#DCE7FD',
      'menu': '#ffffff',
      'highlight-background': '#EFF4FF', // Added new semantic variable
      'attachments-button': '#F0F0F0',
      'textarea-input-field': '#FFFFFF',
      'logo-color': '#FFFFFF',
      'logo-bg': staticPrimaryColor,
      'bg-logo-color': '#F0F0F0',
      'crimson': '#CF274D',
      'soft-sky': '#DAE8F1',
      'orange': '#F79009',
    },
    variables: {
      'border-color': 'var(--v-theme-grey-700)',
      'border-opacity': 0.12,
      'high-emphasis-opacity': 0.9,
      'medium-emphasis-opacity': 1,
      'disabled-opacity': 0.4,
      'hover-opacity': 0.06,
      'focus-opacity': 0.1,
      'selected-opacity': 0.08,
      'activated-opacity': 0.16,
      'pressed-opacity': 0.14,
      'dragged-opacity': 0.1,
      'overlay-scrim-background': '#000000',
      'overlay-scrim-opacity': 0.5,
      'overlay-opacity': 0.6,
      'tooltip-background': '#404040',
      'table-header-color': '#F5F5F5',

      // Shadows
      'shadow-key-umbra-color': '#171717',
      'shadow-xs-opacity': 0.10,
      'shadow-sm-opacity': 0.12,
      'shadow-md-opacity': 0.14,
      'shadow-lg-opacity': 0.16,
      'shadow-xl-opacity': 0.18,
      ...shared,
    },
  },
  dark: {
    dark: true,
    colors: {
      // Standard Colors
      'primary': staticPrimaryColor,
      'on-primary': '#FFFFFF',
      'primary-darken-1': '#9D9AEA', // Lighter shade for hover/active
      'primary-darken-2': '#5C52AF', // Darker shade for hover/active
      'secondary': '#A3A3A3', // Mapped from Gray Iron 500
      'on-secondary': '#171717',
      'secondary-darken-1': '#525252', // Mapped from Gray Iron 600
      'error': staticErrorColor,
      'on-error': '#FFFFFF',
      'error-darken-1': '#F36961', // Lighter shade for hover/active
      'success': '#10B981', // Retained from old theme for completeness
      'on-success': '#FFFFFF',
      'success-darken-1': '#15D493',
      'info': '#06B6D4', // Retained from old theme for completeness
      'on-info': '#FFFFFF',
      'info-darken-1': '#0FE2FF',
      'warning': '#F59E0B', // Retained from old theme for completeness
      'on-warning': '#FFFFFF',
      'warning-darken-1': '#FBBC05',

      // Backgrounds & Surfaces
      'background': '#131313', // Mapped from BG
      'on-background': '#FFFFFF', // Mapped from Text
      'surface': '#171717', // Mapped from Setting_Nav
      'on-surface': '#FFFFFF', // Mapped from Text

      // Gray Iron Scale (Dark)
      'grey-50': '#FAFAFA',
      'grey-100': '#F5F5F5',
      'grey-200': '#E5E5E5',
      'grey-300': '#D4D4D4',
      'grey-400': '#A3A3A3',
      'grey-500': '#737373',
      'grey-600': '#525252',
      'grey-700': '#404040',
      'grey-800': '#262626',
      'grey-900': '#171717',
      'grey-950': '#131313',

      // Brand Scale (Dark)
      'brand-50': '#E5E6FB',
      'brand-100': '#CCCDF6',
      'brand-200': '#B4B4F1',
      'brand-300': '#9D9AEA',
      'brand-400': '#8781E3',
      'brand-600': '#685CC5',
      'brand-700': '#5C52AF',
      'brand-800': '#514799',
      'brand-900': '#453D83',
      'brand-950': '#3A336E',

      // Custom App-Specific Colors (Dark)
      'white': '#FFFFFF',
      'black': '#000000',
      'setting-bg': '#131313',
      'setting-nav': '#171717',
      'login-bg': '#1C1C1E',
      'button-bg': '#262626',
      'toggle-bg': '#FFFFFF',
      'link-color': '#BEB6FF',
      'clear-day': '#1A2238',
      'search-bar': '#242424',
      'pro-background': '#082660',
      'menu': '#2b2b2b',
      'highlight-background': '#1A2238', // Added corresponding dark variant
      'attachments-button': '#404040',
      'textarea-input-field': '#262626',
      'logo-color': staticPrimaryColor,
      'logo-bg': '#FFFFFF',
      'bg-logo-color': '#262626',
      'crimson': '#801A30',
      'soft-sky': '#2C3E50',
      'orange': '#F79009',
    },
    variables: {
      'border-color': 'var(--v-theme-grey-700)',
      'border-opacity': 0.15,
      'high-emphasis-opacity': 0.95,
      'medium-emphasis-opacity': 1,
      'disabled-opacity': 0.38,
      'hover-opacity': 0.08,
      'focus-opacity': 0.12,
      'selected-opacity': 0.1,
      'activated-opacity': 0.18,
      'pressed-opacity': 0.16,
      'dragged-opacity': 0.12,
      'overlay-scrim-background': '#000000',
      'overlay-scrim-opacity': 0.7,
      'overlay-opacity': 0.6,
      'tooltip-background': '#E5E5E5',
      'table-header-color': '#262626',

      // Shadows
      'shadow-key-umbra-color': '#000000',
      'shadow-xs-opacity': 0.18,
      'shadow-sm-opacity': 0.2,
      'shadow-md-opacity': 0.22,
      'shadow-lg-opacity': 0.24,
      'shadow-xl-opacity': 0.28,
      ...shared,
    },
  },
}

export default themes
